import { ChangeDetectionStrategy, Component, Inject, Input } from '@angular/core';
import { CovanceFilter, FilterFieldValue } from '../../covance-filter';
import { EQueryFilterDefinitions } from '../e-query-filter-definitions';
import { EQUERIES_STORAGE, ExternalUserEQueriesStorage } from '../external-user-e-queries-storage';
import { map, publishReplay, refCount, switchMap, startWith, withLatestFrom } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { EQueryTab } from '../models/e-query-tab.enum';
import { GridOptions } from 'ag-grid-community';
import { FormControl } from '@angular/forms';
import { CovanceGridColumn } from '../../covance-ag-grid';
import { ExternalUserEQuery } from '../models/external-user-e-query';
import { ReadEQueryItem, ExternalUserReadEQueryEmitter, ReadEQueryEmitter } from '../read-e-query-emitter';
import { Acknowledgement } from '../../view-history';
import { UserStorage } from '../../user';
import { orderBy } from 'lodash';
import { ExternalUserEQueriesGridDefaultSort } from '../external-user-e-queries-grid-default-sort';

@Component({
     selector: 'external-user-e-queries-grid',
     template: require('./external-user-e-queries-grid.component.html'),
     styles: [require('./external-user-e-queries-grid.component.scss')],
     changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExternalUserEQueriesGridComponent {
    public readonly gridOptions: GridOptions = {
        floatingFilter: false,
        enableFilter: false,
        floatingFiltersHeight: 32
    };
    public readonly protocolControl: FormControl;

    @Input()
    public filterDefs: FilterFieldValue[];

    @Input()
    public columns: CovanceGridColumn[];

    @Input()
    public set siteId(val: number) {
        this.siteId$.next(val);
    }

    @Input()
    public set eQueryTab(value: EQueryTab) {
        this.eQueryTab$.next(value);
        this.groupName = `external-e-query-${value}`;
    }

    @Input()
    public showReadByMe = true;

    public groupName: string;
    public eQueries$: Observable<ExternalUserEQuery[]>;
    public filterFields$: Observable<FilterFieldValue[]>;
    private readonly siteId$ = new BehaviorSubject<number>(undefined);
    private readonly eQueryTab$ = new BehaviorSubject<EQueryTab>(EQueryTab.awaiting);

    constructor(
        @Inject(EQUERIES_STORAGE) private readonly externalEQueriesStorage: ExternalUserEQueriesStorage,
        private readonly filterService: CovanceFilter,
        private readonly eQueryFilterResolver: EQueryFilterDefinitions,
        @Inject(ReadEQueryEmitter) readEQueryEmitter: ExternalUserReadEQueryEmitter,
        private readonly userStorage: UserStorage,
        private readonly gridDefaultSort: ExternalUserEQueriesGridDefaultSort,
    ) {

        this.gridOptions.getRowClass = this.getRowClass;

        const data$ = combineLatest([
                                        this.siteId$,
                                        this.eQueryTab$,
                                    ]);
        this.filterFields$ = this.externalEQueriesStorage
            .getEQueryFilterItems(data$).pipe(
                map(filterItems => {
                    this.filterService.setFiltersItems(filterItems, this.filterDefs);
                    return this.filterDefs;
                }),
                publishReplay(1),
                refCount()
            );

        const filterFlow$ = this.filterFields$
            .pipe(
                switchMap(fields => this.filterService.listen(fields).pipe(map(() => fields))),
                publishReplay(1),
                refCount()
            );

        const eQueries$ = filterFlow$
            .pipe(
                withLatestFrom(data$),
                switchMap(([fields, [siteId, tab]]) =>
                    this.externalEQueriesStorage.getEQueries(siteId, tab, fields).pipe(
                        map(this.sortEQueries),
                    ),
                ),
                publishReplay(1),
                refCount(),
            );

        this.eQueries$ = eQueries$.pipe(
            switchMap(eQueries => {
                return readEQueryEmitter.readEQueryId.pipe(
                    switchMap(readItem => this.readEQuery(readItem, eQueries)),
                    startWith(eQueries),
                );
            }),
            publishReplay(1),
            refCount(),
        );
    }

    private getRowClass = (params: { data: any }): string => {
        if (!this.showReadByMe) {
            return null;
        }

        return params.data && !params.data.isReadByMe
            ? 'strong-row'
            : null;
    }

    private readEQuery(readItem: ReadEQueryItem<ExternalUserEQuery>, eQueries: ExternalUserEQuery[]): Observable<ExternalUserEQuery[]> {
        return this.userStorage.currentUser.pipe(
            map(user => {
                if (!eQueries) {
                    return null;
                }

                const eQuery = eQueries.filter(eq => eq.eQueryId === readItem.eQueryId)[0];

                if (!eQuery) {
                    return eQueries;
                }

                eQuery.isRead = true;

                if (!eQuery.isReadByMe) {
                    eQuery.isReadByMe = true;
                    const ackn = new Acknowledgement();
                    ackn.user = user.metadata;
                    eQuery.viewHistory.push(ackn);
                }

                readItem.afterRead(eQuery);

                return [...eQueries];
            }),
            publishReplay(1),
            refCount(),
        );
    }

    private sortEQueries = (eQueries: ExternalUserEQuery[]): ExternalUserEQuery[] | null => {
        if (!eQueries) {
            return null;
        }

        const sortInfo = this.gridDefaultSort.getByTab(this.eQueryTab$.value);

        return orderBy(eQueries, sortInfo.columns, sortInfo.sortDirection);
    }
}
